import React, { useEffect, useState, useCallback } from "react";
import { CCard, CCardBody, CCardHeader, CButton, CSpinner, CBadge } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilBolt } from "@coreui/icons";
import { getWaiterInsight } from "../../api/hermesApi";
import ErrorAlert from "../UI/Status/ErrorAlert";

/** Same button-triggered, never-auto-fetch UX as the dashboard HermesPanel — GigaChat calls cost tokens. */
export default function WaiterInsightPanel({ serverId, dateFrom, dateTo, groupByRestaurant = false }) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requested, setRequested] = useState(false);

  const fetchInsight = useCallback(
    (force) => {
      if (!serverId || !dateFrom || !dateTo) return;
      setRequested(true);
      setLoading(true);
      setError(null);
      getWaiterInsight({ id: serverId, dateFrom, dateTo, groupByRestaurant, force })
        .then(setInsight)
        .catch(setError)
        .finally(() => setLoading(false));
    },
    [serverId, dateFrom, dateTo, groupByRestaurant],
  );

  useEffect(() => {
    setInsight(null);
    setError(null);
    setRequested(false);
  }, [serverId, dateFrom, dateTo, groupByRestaurant]);

  if (!serverId) return null;
  const content = insight?.content;

  return (
    <CCard className="mt-4">
      <CCardHeader className="d-flex align-items-center justify-content-between py-2">
        <div className="d-flex align-items-center gap-2">
          <CIcon icon={cilBolt} className="text-warning" />
          <span className="fw-semibold">AI-анализ работы официантов</span>
          {insight?.cached && (
            <CBadge color="secondary" title={insight.generated_at}>
              кэш
            </CBadge>
          )}
        </div>
        <CButton
          size="sm"
          color="primary"
          variant="outline"
          onClick={() => fetchInsight(requested)}
          disabled={loading || !dateFrom || !dateTo}
        >
          {loading ? <CSpinner size="sm" /> : requested ? "Обновить анализ" : "Запустить анализ"}
        </CButton>
      </CCardHeader>
      <CCardBody className="pt-3">
        <ErrorAlert error={error} title="Не удалось получить анализ" />

        {insight?.date_clamped && (
          <div className="small text-body-secondary mb-3">
            Сегодняшний день ещё не завершён и исключён из анализа — период скорректирован до{" "}
            {insight.date_to}.
          </div>
        )}

        {!requested && !loading && (
          <div className="text-body-secondary py-2">
            Нажмите «Запустить анализ», чтобы получить AI-разбор работы официантов за выбранный период.
          </div>
        )}

        {loading && !content && (
          <div className="d-flex align-items-center gap-2 text-body-secondary py-2">
            <CSpinner size="sm" />
            Hermes анализирует отчёт…
          </div>
        )}

        {content && (
          <>
            <p className="mb-3">{content.summary}</p>
            {content.highlights?.length > 0 && (
              <div className="mb-3">
                <div className="small fw-semibold text-body-secondary mb-1">Наблюдения</div>
                <ul className="mb-0 ps-3">
                  {content.highlights.map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              </div>
            )}
            {content.risks?.length > 0 && (
              <div className="mb-3">
                <div className="small fw-semibold text-danger mb-1">Риски</div>
                <ul className="mb-0 ps-3">
                  {content.risks.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            )}
            {content.recommendations?.length > 0 && (
              <div>
                <div className="small fw-semibold text-success mb-1">Рекомендации</div>
                <ul className="mb-0 ps-3">
                  {content.recommendations.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CCardBody>
    </CCard>
  );
}
